import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import API_BASE, { withApiCredentials } from "../../api";
import { getActiveAwareAuthToken } from "../../auth/bootstrapTokens";
import { buildShareCountLookup } from "../../utils/postShares";
import useOrdinaryPostLikeMutation from "../../hooks/useOrdinaryPostLikeMutation";
import usePaginatedUserPosts from "../../hooks/usePaginatedUserPosts";
import { favoriteSpotMapEventDetail } from "../../features/favoriteSpots/favoriteSpotOutlookContext";
import useProfileEditPostState from "./edit-post/useProfileEditPostState";
import useProfileOverlayHistory from "./useProfileOverlayHistory";

export default function useProfileState({
  user,
  users = [],
  isOwnProfile,
  focusPostRequest,
  favoriteSpots,
  showFavorites,
  setShowFavorites,
  setActiveTab,
  setShowProfile,
  setSelectedPost,
  setShowInfo,
  setSharePost,
  onDeletePost,
  onUpdatePost,
  editPostMapSelection,
  onEditPostMapSelectionConsumed,
}) {
  const { t } = useTranslation();
  const backLabel = t("back", { defaultValue: "Back" }).replace(
    /^[<\-\u2013\u2014\u2190\u2B05\s]+/,
    ""
  );

  const [openComments, setOpenComments] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [usersMap, setUsersMap] = useState(() =>
    user?.id ? { [user.id]: user } : {}
  );
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openPostMenuId, setOpenPostMenuId] = useState(null);
  const [pendingDeletePostId, setPendingDeletePostId] = useState(null);
  const [deletingPostId, setDeletingPostId] = useState(null);
  const [showCatches, setShowCatches] = useState(false);
  const [showTrips, setShowTrips] = useState(false);
  const [showCoverEdit, setShowCoverEdit] = useState(false);
  const [fullscreenMedia, setFullscreenMedia] = useState(null);
  const [showMediaMenu, setShowMediaMenu] = useState(false);
  const [highlightedCommentKey, setHighlightedCommentKey] = useState(null);
  const [showAvatarPositionModal, setShowAvatarPositionModal] = useState(false);
  const [avatarPositionFile, setAvatarPositionFile] = useState(null);
  const [showCoverPositionModal, setShowCoverPositionModal] = useState(false);
  const [coverPositionFile, setCoverPositionFile] = useState(null);
  const [savingMediaMode, setSavingMediaMode] = useState(null);
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState("");
  const [coverPreviewUrl, setCoverPreviewUrl] = useState("");
  const { togglePostLike, isPostLikePending } =
    useOrdinaryPostLikeMutation();
  const profilePostPagination = usePaginatedUserPosts(user?.id);
  const posts = profilePostPagination.posts;
  const [coverFrameAspectRatio, setCoverFrameAspectRatio] = useState(
    1100 / 460
  );

  const mobileMenuRef = useRef(null);
  const mobileBurgerRef = useRef(null);
  const avatarInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const profileHeaderRef = useRef(null);
  const postRefs = useRef({});
  const commentRefs = useRef({});
  const avatarPreviewUrlRef = useRef("");
  const coverPreviewUrlRef = useRef("");
  const showMyCatchesButton = false;
  const shareCountLookup = buildShareCountLookup(posts);
  const focusPostId = focusPostRequest?.postId;
  const focusCommentId = focusPostRequest?.commentId;
  const focusPostRequestedAt = focusPostRequest?.requestedAt;
  const followersCount = Array.isArray(user?.followers) ? user.followers.length : 0;
  const followingCount = Array.isArray(user?.following) ? user.following.length : 0;
  const friendsCount = Array.isArray(user?.friends) ? user.friends.length : 0;
  const {
    editingPost,
    setEditingPost,
    handleCloseEditPost,
    handleOpenEditPost,
    handleEditPostSaved: saveEditedPost,
  } = useProfileEditPostState({
    posts,
    editPostMapSelection,
    onEditPostMapSelectionConsumed,
    onUpdatePost: (updatedPost, previousPost) => {
      profilePostPagination.updatePost(updatedPost);
      onUpdatePost?.(updatedPost, previousPost);
    },
  });
  const handleCloseFavorites = useCallback(() => {
    setShowFavorites(false);
  }, [setShowFavorites]);
  const handleCloseTrips = useCallback(() => setShowTrips(false), []);
  const handleCloseCatches = useCallback(() => setShowCatches(false), []);

  const handleCloseFullscreenMedia = useCallback(() => {
    setFullscreenMedia(null);
    setShowMediaMenu(false);
  }, []);

  const handleCloseCoverEdit = useCallback(() => setShowCoverEdit(false), []);

  const handleAvatarPositionCancel = useCallback(() => {
    if (savingMediaMode === "avatar") return;
    setShowAvatarPositionModal(false);
    setAvatarPositionFile(null);
  }, [savingMediaMode]);

  const handleCloseProfileLayers = useCallback(() => {
    setMobileMenuOpen(false);
    setShowFavorites(false);
    setShowTrips(false);
    setShowCatches(false);
    setFullscreenMedia(null);
    setShowMediaMenu(false);
    setShowCoverEdit(false);
    setShowAvatarPositionModal(false);
    setAvatarPositionFile(null);
    setShowCoverPositionModal(false);
    setCoverPositionFile(null);
    setEditingPost(null);
    setPendingDeletePostId(null);
  }, [setEditingPost, setShowFavorites]);

  useEffect(() => {
    return () => {
      if (avatarPreviewUrlRef.current) {
        URL.revokeObjectURL(avatarPreviewUrlRef.current);
      }
      if (coverPreviewUrlRef.current) {
        URL.revokeObjectURL(coverPreviewUrlRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (avatarPreviewUrlRef.current) {
      URL.revokeObjectURL(avatarPreviewUrlRef.current);
      avatarPreviewUrlRef.current = "";
    }
    if (coverPreviewUrlRef.current) {
      URL.revokeObjectURL(coverPreviewUrlRef.current);
      coverPreviewUrlRef.current = "";
    }
    setAvatarPreviewUrl("");
    setCoverPreviewUrl("");
  }, [user?.id]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    if (typeof window === "undefined" || window.innerWidth > 768) return;

    const handlePointerDownOutsideMobileMenu = (event) => {
      const clickedInsideMenu = mobileMenuRef.current?.contains(event.target);
      const clickedBurgerButton = mobileBurgerRef.current?.contains(event.target);

      if (!clickedInsideMenu && !clickedBurgerButton) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDownOutsideMobileMenu);
    return () => {
      document.removeEventListener(
        "pointerdown",
        handlePointerDownOutsideMobileMenu
      );
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (!openPostMenuId) return;

    const handlePointerDownOutsidePostMenu = (event) => {
      if (
        event.target instanceof Element &&
        event.target.closest(".profile-post-owner-actions")
      ) {
        return;
      }
      setOpenPostMenuId(null);
    };

    document.addEventListener("pointerdown", handlePointerDownOutsidePostMenu);
    return () => {
      document.removeEventListener(
        "pointerdown",
        handlePointerDownOutsidePostMenu
      );
    };
  }, [openPostMenuId]);

  useEffect(() => {
    if (Array.isArray(users) && users.length > 0) {
      const map = {};
      users.forEach((loadedUser) => {
        if (loadedUser?.id) {
          map[loadedUser.id] = loadedUser;
        }
      });
      setUsersMap(map);
      return;
    }

    if (isOwnProfile && user?.id) {
      setUsersMap({ [user.id]: user });
      return;
    }

    let isMounted = true;

    async function loadUsers() {
      try {
        const res = await fetch(`${API_BASE}/auth/users`);
        if (!res.ok) return;
        const loadedUsers = await res.json();
        const map = {};
        loadedUsers.forEach((loadedUser) => {
          map[loadedUser.id] = loadedUser;
        });
        if (isMounted) {
          setUsersMap(map);
        }
      } catch (err) {
        console.error("loadUsers:", err);
      }
    }

    loadUsers();
    return () => {
      isMounted = false;
    };
  }, [isOwnProfile, user, users]);

  useEffect(() => {
    if (!focusPostId) return;

    const timeoutId = window.setTimeout(() => {
      const targetNode = postRefs.current?.[focusPostId];
      if (!targetNode) return;
      targetNode.scrollIntoView({ behavior: "smooth", block: "center" });
      targetNode.focus?.({ preventScroll: true });
    }, 80);

    return () => window.clearTimeout(timeoutId);
  }, [focusPostId, focusPostRequestedAt, posts]);

  useEffect(() => {
    if (!focusPostId || !focusCommentId) return;

    setOpenComments(focusPostId);
    setReplyTo(null);
    setReplyText("");
  }, [focusPostId, focusCommentId, focusPostRequestedAt]);

  useEffect(() => {
    if (!focusPostId || !focusCommentId || openComments !== focusPostId) {
      return undefined;
    }

    const commentKey = `${focusPostId}:${focusCommentId}`;
    let retryTimeoutId = null;
    let animationFrameId = null;
    let clearHighlightTimeoutId = null;
    let attempts = 0;
    let cancelled = false;

    const focusTargetComment = () => {
      if (cancelled) return;

      const targetNode = commentRefs.current?.[commentKey];
      if (!targetNode) {
        if (attempts >= 20) return;
        attempts += 1;
        retryTimeoutId = window.setTimeout(() => {
          animationFrameId = window.requestAnimationFrame(focusTargetComment);
        }, 80);
        return;
      }

      targetNode.scrollIntoView({ behavior: "smooth", block: "center" });
      targetNode.focus?.({ preventScroll: true });
      setHighlightedCommentKey(commentKey);

      clearHighlightTimeoutId = window.setTimeout(() => {
        setHighlightedCommentKey((current) =>
          current === commentKey ? null : current
        );
      }, 2500);
    };

    animationFrameId = window.requestAnimationFrame(focusTargetComment);

    return () => {
      cancelled = true;
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
      if (retryTimeoutId) {
        window.clearTimeout(retryTimeoutId);
      }
      if (clearHighlightTimeoutId) {
        window.clearTimeout(clearHighlightTimeoutId);
      }
    };
  }, [focusPostId, focusCommentId, focusPostRequestedAt, openComments, posts]);

  const resetCoverInput = useCallback(() => {
    if (coverInputRef.current) {
      coverInputRef.current.value = "";
    }
  }, []);

  const handleCoverPositionCancel = useCallback(() => {
    if (savingMediaMode === "cover") return;
    setShowCoverPositionModal(false);
    setCoverPositionFile(null);
    resetCoverInput();
  }, [resetCoverInput, savingMediaMode]);

  const setLocalMediaPreview = useCallback((mode, file) => {
    if (!(file instanceof File)) return;

    const nextUrl = URL.createObjectURL(file);

    if (mode === "avatar") {
      if (avatarPreviewUrlRef.current) {
        URL.revokeObjectURL(avatarPreviewUrlRef.current);
      }
      avatarPreviewUrlRef.current = nextUrl;
      setAvatarPreviewUrl(nextUrl);
      return;
    }

    if (coverPreviewUrlRef.current) {
      URL.revokeObjectURL(coverPreviewUrlRef.current);
    }
    coverPreviewUrlRef.current = nextUrl;
    setCoverPreviewUrl(nextUrl);
  }, []);

  const openCoverPositionModal = useCallback(
    (file) => {
      if (!file) return;

      const rect = profileHeaderRef.current?.getBoundingClientRect();
      if (rect?.width && rect?.height) {
        setCoverFrameAspectRatio(rect.width / rect.height);
      } else {
        setCoverFrameAspectRatio(1100 / 460);
      }

      setCoverPositionFile(file);
      setShowCoverPositionModal(true);
      resetCoverInput();
    },
    [resetCoverInput]
  );

  const profileOverlayHistory = useProfileOverlayHistory({
    mobileMenuOpen,
    setMobileMenuOpen,
    showFavorites,
    handleCloseFavorites,
    showTrips,
    handleCloseTrips,
    showCatches,
    handleCloseCatches,
    fullscreenMedia,
    handleCloseFullscreenMedia,
    showCoverEdit,
    handleCloseCoverEdit,
    showAvatarPositionModal,
    handleAvatarPositionCancel,
    showCoverPositionModal,
    handleCoverPositionCancel,
    editingPost,
    handleCloseEditPost,
  });

  const handleEditPostSaved = useCallback((updatedPost, options) => {
    if (options?.close === false) {
      profileOverlayHistory.requestSuppressNavigationSync?.();
    }
    saveEditedPost(updatedPost, options);
  }, [profileOverlayHistory, saveEditedPost]);

  const handleDeletePost = (postId) => {
    if (!onDeletePost || deletingPostId || !postId) return;

    setOpenPostMenuId(null);
    setPendingDeletePostId(postId);
  };

  const handleCancelDeletePost = useCallback(() => {
    if (deletingPostId) return;
    setPendingDeletePostId(null);
  }, [deletingPostId]);

  const handleConfirmDeletePost = async () => {
    const postId = pendingDeletePostId;
    if (!onDeletePost || deletingPostId || !postId) return;

    setDeletingPostId(postId);
    try {
      const deleteResult = await onDeletePost(postId);
      const didDelete = deleteResult === true || deleteResult?.ok === true;
      if (!didDelete) {
        window.alert(
          deleteResult?.message || t("profile_delete_post_failed")
        );
        return;
      }

      setPendingDeletePostId(null);
      setOpenPostMenuId(null);
      profilePostPagination.removePost(postId);
      if (openComments === postId) {
        setOpenComments(null);
      }
      if (replyTo) {
        setReplyTo(null);
      }
      setReplyText("");
      setCommentText("");
    } finally {
      setDeletingPostId((current) => (current === postId ? null : current));
    }
  };

  const savePositionedImage = async (mode, payload) => {
    const token = getActiveAwareAuthToken(window);
    const hasSessionUser = Boolean(user?.id);
    if (!token && !hasSessionUser) return null;

    const formData = new FormData();
    if (token) {
      formData.append("token", token);
    }

    if (mode === "avatar") {
      const avatarFile = payload?.file;
      if (!(avatarFile instanceof File)) return null;

      formData.append("avatar", avatarFile);
      formData.append("avatarPositionX", String(payload?.avatarPositionX ?? 50));
      formData.append("avatarPositionY", String(payload?.avatarPositionY ?? 50));
      formData.append("avatarScale", String(payload?.avatarScale ?? 1));
    }

    if (mode === "cover") {
      const coverFile = payload?.file;
      if (!(coverFile instanceof File)) return null;

      formData.append("cover", coverFile);
      formData.append("coverPositionX", String(payload?.coverPositionX ?? 50));
      formData.append("coverPositionY", String(payload?.coverPositionY ?? 50));
      formData.append("coverScale", String(payload?.coverScale ?? 1));
    }

    try {
      const res = await fetch(
        `${API_BASE}/auth/update`,
        withApiCredentials({
          method: "POST",
          body: formData,
        })
      );

      if (!res.ok) {
        alert(
          mode === "avatar"
            ? t("avatar_update_failed")
            : t("cover_update_error")
        );
        return null;
      }

      const updatedUser = await res.json();

      window.dispatchEvent(
        new CustomEvent("userUpdated", { detail: updatedUser })
      );
      return updatedUser;

    } catch (err) {
      console.error(err);
      alert(
        mode === "avatar" ? t("avatar_update_error") : t("cover_update_error")
      );
      return null;
    }
  };

  const handleCloseProfile = () => {
    handleCloseProfileLayers();
    setShowProfile(false);
  };

  const handleToggleMobileMenu = () => {
    setMobileMenuOpen((current) => !current);
  };

  const handleMobileCloseProfile = () => {
    handleCloseProfileLayers();
    setShowProfile(false);
  };

  const handleOpenFavorites = () => {
    setShowFavorites(true);
  };

  const handleMobileOpenFavorites = () => {
    setShowFavorites(true);
    setMobileMenuOpen(false);
  };

  const handleOpenTrips = () => {
    setShowTrips(true);
  };

  const handleMobileOpenTrips = () => {
    setShowTrips(true);
    setMobileMenuOpen(false);
  };

  const handleOpenCatches = () => {
    setShowCatches(true);
  };

  const handleMobileOpenCatches = () => {
    setShowCatches(true);
    setMobileMenuOpen(false);
  };

  const handleFavoriteSpotOpen = (spot) => {
    setActiveTab("map");
    setShowFavorites(false);
    setShowProfile(false);
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("flyToSpot", {
          detail: favoriteSpotMapEventDetail(spot),
        })
      );
    }, 400);
  };

  const handleAvatarInputChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setAvatarPositionFile(file);
    setShowAvatarPositionModal(true);
  };

  const handleCoverInputChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    openCoverPositionModal(file);
  };

  const handleCoverEditInputChange = (event) => {
    handleCoverInputChange(event);
    setShowCoverEdit(false);
  };

  const handleOpenAvatarFullscreen = () => {
    setFullscreenMedia({
      type: "avatar",
      src: avatarPreviewUrl || (user?.avatar
        ? `${API_BASE}${user.avatar}?t=${user.updatedAt || Date.now()}`
        : "https://cdn-icons-png.flaticon.com/512/847/847969.png"),
    });
  };

  const handleOpenCoverFullscreen = () => {
    if (!user?.cover) {
      coverInputRef.current?.click();
      return;
    }

    setFullscreenMedia({
      type: "cover",
      src: coverPreviewUrl || `${API_BASE}${user.cover}?t=${user.updatedAt || Date.now()}`,
    });
  };

  const handleFullscreenCameraClick = (event) => {
    event.stopPropagation();
    setShowMediaMenu(false);

    if (fullscreenMedia?.type === "avatar") {
      avatarInputRef.current?.click();
    } else {
      coverInputRef.current?.click();
    }
  };

  const handleCloseMediaMenu = () => {
    setShowMediaMenu(false);
  };

  const handleMediaMenuAddPhoto = () => {
    setShowMediaMenu(false);

    if (fullscreenMedia?.type === "avatar") {
      avatarInputRef.current?.click();
    } else {
      setShowCoverEdit(true);
    }
  };

  const handleTogglePostMenu = (postId) => {
    setOpenPostMenuId((current) => (current === postId ? null : postId));
  };

  const handleOpenPostEditMenuItem = (post) => {
    handleOpenEditPost(post);
    setOpenPostMenuId(null);
  };

  const handleLikePost = (post) =>
    togglePostLike({
      post,
      actorUserId: user?.id,
      errorMessage: t("post_like_update_failed", {
        defaultValue: "Could not update the like. Please try again.",
      }),
    });

  const handleToggleComments = (postId) => {
    const next = openComments === postId ? null : postId;
    setOpenComments(next);
    setReplyTo(null);
    setReplyText("");
    setCommentText("");
  };

  const handleOpenShare = (post) => {
    setSharePost(post);
  };

  const handleOpenInfo = (post) => {
    setSelectedPost(post);
    setShowInfo(true);
  };

  const handleLikeComment = async (postId, commentId) => {
    const token = localStorage.getItem("token");

    try {
      const fd = new FormData();
      if (token) {
        fd.append("token", token);
      }
      fd.append("post_id", postId);
      fd.append("comment_id", commentId);

      const res = await fetch(
        `${API_BASE}/posts/comment/like`,
        withApiCredentials({
          method: "POST",
          body: fd,
        })
      );
      if (!res.ok) throw new Error("comment like failed");

      const data = await res.json();

      window.dispatchEvent(
        new CustomEvent("commentLiked", {
          detail: {
            postId,
            commentId,
            likes: data.likes,
          },
        })
      );
    } catch (err) {
      console.error("comment like:", err);
    }
  };

  const handleToggleReply = (commentId) => {
    setReplyTo((current) => (current === commentId ? null : commentId));
  };

  const handleLikeReply = async (postId, commentId, replyId) => {
    const token = localStorage.getItem("token");

    try {
      const fd = new FormData();
      if (token) {
        fd.append("token", token);
      }
      fd.append("post_id", postId);
      fd.append("comment_id", commentId);
      fd.append("reply_id", replyId);

      const res = await fetch(
        `${API_BASE}/posts/comment/reply/like`,
        withApiCredentials({
          method: "POST",
          body: fd,
        })
      );
      if (!res.ok) throw new Error("reply like failed");

      const data = await res.json();

      window.dispatchEvent(
        new CustomEvent("replyLiked", {
          detail: {
            postId,
            commentId,
            replyId,
            likes: data.likes,
          },
        })
      );
    } catch (err) {
      console.error("reply like:", err);
    }
  };

  const handleSubmitReply = async (postId, commentId) => {
    const token = localStorage.getItem("token");
    if (!replyText.trim()) return;

    try {
      const fd = new FormData();
      if (token) {
        fd.append("token", token);
      }
      fd.append("post_id", postId);
      fd.append("comment_id", commentId);
      fd.append("text", replyText);

      const res = await fetch(
        `${API_BASE}/posts/comment/reply`,
        withApiCredentials({
          method: "POST",
          body: fd,
        })
      );
      if (!res.ok) throw new Error("reply failed");

      const data = await res.json();

      window.dispatchEvent(
        new CustomEvent("commentReplied", {
          detail: {
            postId,
            commentId,
            reply: data.reply,
          },
        })
      );

      setReplyText("");
      setReplyTo(null);
    } catch (err) {
      console.error("reply:", err);
    }
  };

  const handleSubmitComment = async (postId) => {
    const token = localStorage.getItem("token");
    if (!commentText.trim()) return;

    try {
      const fd = new FormData();
      if (token) {
        fd.append("token", token);
      }
      fd.append("post_id", postId);
      fd.append("text", commentText);

      const res = await fetch(
        `${API_BASE}/posts/comment`,
        withApiCredentials({
          method: "POST",
          body: fd,
        })
      );
      if (!res.ok) throw new Error("comment failed");

      const data = await res.json();

      window.dispatchEvent(
        new CustomEvent("postCommented", {
          detail: {
            postId,
            comment: data.comment,
          },
        })
      );

      setCommentText("");
      setOpenComments(null);
      setReplyTo(null);
      setReplyText("");
    } catch (err) {
      console.error("comment:", err);
    }
  };

  const handleAvatarPositionConfirm = async (payload) => {
    if (savingMediaMode) return;

    setSavingMediaMode("avatar");
    try {
      const updatedUser = await savePositionedImage("avatar", payload);
      if (!updatedUser) return;

      setLocalMediaPreview("avatar", payload?.file);
      setShowAvatarPositionModal(false);
      setAvatarPositionFile(null);
      setFullscreenMedia(null);
      setShowMediaMenu(false);
      setShowCoverEdit(false);
    } finally {
      setSavingMediaMode(null);
    }
  };

  const handleCoverPositionConfirm = async (payload) => {
    if (savingMediaMode) return;

    setSavingMediaMode("cover");
    try {
      const updatedUser = await savePositionedImage("cover", payload);
      if (!updatedUser) return;

      setLocalMediaPreview("cover", payload?.file);
      setShowCoverPositionModal(false);
      setCoverPositionFile(null);
      resetCoverInput();
      setFullscreenMedia(null);
      setShowMediaMenu(false);
      setShowCoverEdit(false);
    } finally {
      setSavingMediaMode(null);
    }
  };

  return {
    t,
    user,
    directoryUsers: users,
    isOwnProfile,
    posts,
    profilePostsLoading: profilePostPagination.loading,
    profilePostsLoadingMore: profilePostPagination.loadingMore,
    profilePostsHasMore: profilePostPagination.hasMore,
    loadMoreProfilePosts: profilePostPagination.loadMore,
    favoriteSpots,
    showFavorites,
    backLabel,
    shareCountLookup,
    followersCount,
    followingCount,
    friendsCount,
    showMyCatchesButton,
    openComments,
    commentText,
    replyTo,
    replyText,
    usersMap,
    mobileMenuOpen,
    openPostMenuId,
    pendingDeletePostId,
    deletingPostId,
    editingPost,
    isPostLikePending,
    showCatches,
    showTrips,
    showCoverEdit,
    fullscreenMedia,
    showMediaMenu,
    highlightedCommentKey,
    showAvatarPositionModal,
    avatarPositionFile,
    showCoverPositionModal,
    coverPositionFile,
    savingMediaMode,
    avatarPreviewUrl,
    coverPreviewUrl,
    coverFrameAspectRatio,
    mobileMenuRef,
    mobileBurgerRef,
    avatarInputRef,
    coverInputRef,
    profileHeaderRef,
    postRefs,
    commentRefs,
    setCommentText,
    setReplyText,
    handleCloseProfile,
    handleToggleMobileMenu,
    handleMobileCloseProfile,
    handleOpenFavorites,
    handleMobileOpenFavorites,
    handleCloseFavorites,
    handleOpenTrips,
    handleMobileOpenTrips,
    handleCloseTrips,
    handleOpenCatches,
    handleMobileOpenCatches,
    handleCloseCatches,
    handleFavoriteSpotOpen,
    handleAvatarInputChange,
    handleCoverInputChange,
    handleCoverEditInputChange,
    handleOpenAvatarFullscreen,
    handleOpenCoverFullscreen,
    handleCloseFullscreenMedia,
    handleFullscreenCameraClick,
    handleCloseMediaMenu,
    handleMediaMenuAddPhoto,
    handleCloseCoverEdit,
    handleDeletePost,
    handleCancelDeletePost,
    handleConfirmDeletePost,
    handleOpenEditPost: handleOpenPostEditMenuItem,
    handleCloseEditPost,
    handleEditPostSaved,
    handleTogglePostMenu,
    handleLikePost,
    handleToggleComments,
    handleOpenShare,
    handleOpenInfo,
    handleLikeComment,
    handleToggleReply,
    handleLikeReply,
    handleSubmitReply,
    handleSubmitComment,
    handleAvatarPositionCancel,
    handleAvatarPositionConfirm,
    handleCoverPositionCancel,
    handleCoverPositionConfirm,
  };
}
